import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { EntityState } from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import type { Address } from "@@/shared/locations/address";
import { addressZodSchema, emptyAddress } from "@@/shared/locations/address";
import type { Except } from "type-fest";
import { z } from "zod";
import type { UserAddressId } from "./user-address-id";
import {
    userAddressIdFactory,
    userAddressIdZodSchema,
} from "./user-address-id";
import type { UserId } from "./user-id";
import { userIdZodSchema } from "./user-id";

type UserAddressInputType = "GOOGLE_AUTO_COMPLATE" | "MANUAL";
type UserAddress = {
    _id: UserAddressId;
    _type: "USERADDRESS";
    _status: EntityState;
    _createdAt: IsoAndUnixTimestamp;
    userId: UserId;
    address: Address;
    /**
     * Title, like; Home, My parents, Sommarstugan
     * @type {string}
     */
    name: string;
    /**
     * Våning, portkod etc
     * @type {string}
     */
    details: string;
    /**
     * Default instructions for delivery man for the address
     * @type {string}
     */
    deliveryInstructions: string;
    /**
     * Was the address created with google auto complete or manually selected and written?
     * Default to GOOGLE_AUTO_COMPLATE when undefined (old addresses were only available as autocompleted)
     * @type {UserAddressInputType}
     */
    inputType?: UserAddressInputType;
};
type AnonymousUserAddress = Except<UserAddress, "userId"> & { userId?: UserId };

const userAddressFactory = (
    params: Except<UserAddress, "_id" | "_createdAt" | "_status" | "_type"> & {
        _id?: UserAddressId;
    },
): UserAddress => {
    if (params._id && !userAddressIdZodSchema.safeParse(params._id).success) {
        throw new Error("Invalid user address id");
    }
    return {
        ...params,
        _id: params._id ?? userAddressIdFactory(),
        _type: "USERADDRESS",
        _status: "ACTIVE",
        _createdAt: nowIsoAndUnix(),
    };
};
type AnonymousUserAddressFactoryParams = Except<
    AnonymousUserAddress,
    "_id" | "_createdAt" | "_status" | "_type"
> & { _id?: UserAddressId };

const anonymousUserAddressFactory = (
    params: AnonymousUserAddressFactoryParams,
): AnonymousUserAddress => {
    if (params._id && !userAddressIdZodSchema.safeParse(params._id).success) {
        throw new Error("Invalid user address id");
    }
    return {
        ...params,
        _id: params._id ?? userAddressIdFactory(),
        _type: "USERADDRESS",
        _status: "ACTIVE",
        _createdAt: nowIsoAndUnix(),
    };
};

type EmptyUserAddressFactoryParams = Pick<UserAddress, "userId" | "inputType">;
const emptyUserAddressFactory = (
    params: EmptyUserAddressFactoryParams,
): UserAddress => {
    return userAddressFactory({
        address: emptyAddress,
        deliveryInstructions: "",
        details: "",
        name: "",
        userId: params.userId,
        inputType: params.inputType ?? "GOOGLE_AUTO_COMPLATE",
    });
};

type AnonymousEmptyUserAddressFactoryParams = Pick<
    AnonymousUserAddress,
    "inputType" | "userId"
>;
const anonymousEmptyUserAddressFactory = (
    params: AnonymousEmptyUserAddressFactoryParams,
): AnonymousUserAddress => {
    return anonymousUserAddressFactory({
        address: emptyAddress,
        deliveryInstructions: "",
        details: "",
        name: "",
        userId: params.userId,
        inputType: params.inputType ?? "GOOGLE_AUTO_COMPLATE",
    });
};

const userAddressZodSchema: z.ZodSchema<UserAddress> = z.object({
    _id: userAddressIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,
    _type: z.literal("USERADDRESS"),
    address: addressZodSchema,
    deliveryInstructions: z.string(),
    inputType: z.enum(["GOOGLE_AUTO_COMPLATE", "MANUAL"]),
    details: z.string(),
    name: z.string(),
    userId: userIdZodSchema,
});

const anonymousUserAddressZodSchema = z.object({
    _id: userAddressIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,
    _type: z.literal("USERADDRESS"),
    address: addressZodSchema,
    deliveryInstructions: z.string(),
    inputType: z.enum(["GOOGLE_AUTO_COMPLATE", "MANUAL"]),
    details: z.string(),
    name: z.string(),
    userIdZodSchema: userIdZodSchema,
});

export {
    anonymousEmptyUserAddressFactory,
    anonymousUserAddressZodSchema,
    emptyUserAddressFactory,
    userAddressFactory,
    userAddressZodSchema,
};
export type { UserAddress, UserAddressInputType };
